import { MailboxIcon, MapIcon, ShieldX as Shield } from 'lucide-react';

import { Button, Separator } from '@/_components';
import { Alert } from '@/_components/alert';
import { openGoogleLocation, snackbar } from '@/_shared';

import { addressUspsValidatedLucideIcon } from './components/address-usps-validated-lucide';
import { hasAddress } from './shared';
import type { AddressSectionLayoutProps } from './types';

import { SectionHeading } from '../../headers';
import { CardContainer } from '../../layout';

export default function AddressSectionLayout({
  text,
  icon = MailboxIcon,
  isEdit,
  children,
  noLayout = false,
  value,
  canValidate = false,
  isValidatingAddress = false,
  isValidated = false,
  showUpdateReminder = false,
  onValidateAddress,
}: AddressSectionLayoutProps) {
  const hasAddressValue = Boolean(value && hasAddress(value) && value.address1);

  const handleViewAddress = () => {
    if (isEdit || !hasAddressValue || !value) {
      return;
    }
    const { address1, address2, state, city, postal_code, country } = value!;

    openGoogleLocation({
      address1: address1 || '',
      address2,
      zipCode: postal_code || '',
      city: city || '',
      state: state?.abbreviation ?? state?.name ?? '',
      country: country?.name || '',
    });
  };

  const handleValidateClick = () => {
    if (isValidated) {
      snackbar.success('Address has already been validated.');
      return;
    }
    onValidateAddress?.();
  };

  const showValidateButton = canValidate && !!onValidateAddress;
  const showViewInMap = !isEdit && hasAddressValue;

  const content = (
    <>
      <SectionHeading
        text={text}
        icon={icon}
        rightSlot={
          <div className="flex items-center gap-2">
            {showValidateButton && (
              <Button
                iconLeft={isValidated ? addressUspsValidatedLucideIcon : Shield}
                as="link"
                className="dark:text-primary"
                size="sm"
                showLoader={isValidatingAddress}
                disabled={isValidatingAddress}
                onClick={handleValidateClick}
              >
                {isValidated ? 'Validated' : 'Validate Address'}
              </Button>
            )}
            {showViewInMap && (
              <>
                {showValidateButton && <Separator orientation="vertical" className="h-4!" />}
                <Button
                  iconLeft={MapIcon}
                  as="link"
                  className="dark:text-primary -mr-4"
                  size="sm"
                  onClick={handleViewAddress}
                >
                  View in Map
                </Button>
              </>
            )}
          </div>
        }
      />
      {children}
      {showUpdateReminder && (
        <Alert type="info" className="mt-3">
          Address has some pending changes. Click &lsquo;Update&rsquo; to save your changes.
        </Alert>
      )}
    </>
  );

  return noLayout ? content : <CardContainer>{content}</CardContainer>;
}
